/**
 * A recorded plot request (`audit.usage_event.request`) back into an Explore
 * link, so an administrator can open what somebody plotted.
 *
 * The request is the backend's `PlotRequest`. It is turned into the assistant's
 * `PlotSpec`, whose field names are mostly the same, and serialized by
 * `specToQuery` - the one path that already turns a plot description into a
 * URL Explore restores and plots. Filter rows and operand-style derived signals
 * are not part of `PlotSpec`, so they are added here in the URL's own format.
 */

import { specToQuery } from '../assistant/apply-plot.ts';
import type { PlotSpec } from '../../api/types.ts';

type Req = Record<string, unknown>;

interface ReqFilter {
  column: string;
  op: string;
  value?: unknown;
  value_column?: string | null;
}

interface ReqDerived {
  name: string;
  op?: string;
  expr?: string | null;
  operand_a?: string | null;
  operand_a_const?: number | null;
  operand_b_col?: string | null;
  operand_b_const?: number | null;
}

const list = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

/** A derived signal as an expression, whichever way it was written. */
export function derivedExpr(d: ReqDerived): string | null {
  if (d.expr?.trim()) return d.expr;
  const a = d.operand_a ?? d.operand_a_const;
  const b = d.operand_b_col ?? d.operand_b_const;
  return a == null || b == null ? null : `${a} ${d.op ?? '*'} ${b}`;
}

/** The Explore query (no leading `?`) that reopens this request. */
export function requestToQuery(req: Req): string {
  const derived = (Array.isArray(req.derived) ? (req.derived as ReqDerived[]) : [])
    .map((d) => ({ name: d.name, expr: derivedExpr(d) }))
    .filter((d): d is { name: string; expr: string } => !!d.expr);
  const spec = {
    ...req,
    ship_id: String(req.ship_id ?? ''),
    source: req.source === 'live' ? 'live' : 'historical',
    plot_type: req.plot_type,
    y_left: list(req.y_left),
    y_right: list(req.y_right),
    hist_columns: list(req.hist_columns),
    derived,
    start_date: typeof req.start_date === 'string' ? req.start_date : null,
    end_date: typeof req.end_date === 'string' ? req.end_date : null,
  } as PlotSpec;
  const params = new URLSearchParams(specToQuery(spec));

  const group = req.filters as { logic?: string; conditions?: ReqFilter[] } | null | undefined;
  const rows = (group?.conditions ?? [])
    .filter((f) => f.op !== 'in') // a slicer's ticked values; they have no row form
    .map((f) => `${f.column}|${f.op}|${f.value_column ? '@' + f.value_column : String(f.value ?? '')}`);
  if (rows.length) {
    const slicers = params.get('filters');
    params.set('filters', [...rows, ...(slicers ? [slicers] : [])].join(';;'));
    if (group?.logic === 'OR') params.set('logic', 'OR');
  }
  return params.toString();
}

/** Open the request in Explore in a new tab, which plots it on load. */
export function openRequestInExplore(req: Req): void {
  window.open(`/explore/plot?${requestToQuery(req)}`, '_blank', 'noopener');
}

const SIGNAL_LISTS = ['y_left', 'y_right', 'hist_columns', 'corr_columns', 'pie_columns', 'box_columns', 'table_columns', 'columns'];
const SIGNAL_SINGLES = ['y_column', 'z_column', 'color_by', 'number_column', 'latitude_column', 'longitude_column', 'box_column', 'pie_band_column'];
const SIGNAL_OBJECTS = ['table_measures', 'gauge_dials', 'sankey_values'];

/** Every signal a request names, once each, in the order it names them. */
export function signalsOf(req: Req): string[] {
  const out: string[] = [];
  for (const k of SIGNAL_LISTS) out.push(...list(req[k]));
  for (const k of SIGNAL_SINGLES) if (typeof req[k] === 'string' && req[k]) out.push(req[k] as string);
  for (const k of SIGNAL_OBJECTS) {
    for (const o of Array.isArray(req[k]) ? (req[k] as Array<{ column?: string | null }>) : []) {
      if (o?.column) out.push(o.column);
    }
  }
  return [...new Set(out)];
}

/** `/dashboards/42` → "Dashboards": where in the app a plot was asked for. */
export function pageLabel(page: string | null | undefined): string {
  const first = (page ?? '').split(/[/?#]/).find(Boolean);
  if (!first) return 'Home';
  return first.charAt(0).toUpperCase() + first.slice(1).replace(/-/g, ' ');
}
